import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSeoMeta, useHead } from '@unhead/react';
import { propertyApi, Property } from '../services/api';
import NearbyPropertiesModal from '../components/NearbyPropertiesModal';
// Concept A 정책 강화: PriceTag + PropertyCard + 상태 마스킹 + EmptyState 모두 design-system 경유로 통일
import {
  PriceTag,
  PropertyCard as DSPropertyCard,
  StickyHeader,
  DesktopNav,
  DesktopPhonePill,
  BrandLogo,
  EmptyState,
  type PropertyCardData,
} from '../design-system/components';
import { maskPropertyStatus, isUpcomingBadge, isSellingNow, formatUnits } from '../domain/property/formatters';
import { buildBadges } from '../domain/property/badges';
import { getPropertyUrl, getPropertyFullUrl } from '../utils/formatters';
import { shouldNoindex, generateCanonicalUrl } from '../utils/seoUtils';

// design-system PropertyCard용 데이터 변환 (HomePage 컨텍스트)
const buildHomeImageUrl = (raw?: string | null): string | null => {
  if (!raw) return null;
  if (raw.startsWith('http') || raw.startsWith('/')) return raw;
  return `/media/${raw}`;
};

const toHomeCardData = (p: Property): PropertyCardData => ({
  id: p.id,
  title: p.title,
  locationLabel: (p.address ?? '').split(/\s+/).slice(0, 2).join(' '),
  imageUrl: buildHomeImageUrl((p as any).image_card_url || (p as any).image_thumbnail_url || p.image_url),
  status: p.status,
  isPreRegistration: isUpcomingBadge(p.status, (p as any).is_pre_registration === true),
  metaLine: formatUnits(p as any, { short: true }) || undefined,
  minPrice: p.min_price,
  maxPrice: p.max_price,
  badges: buildBadges(p), // v2: 대표혜택 → 할인율 → 분양예정 (최대 3)
});

// HomePage 카드용 상태 chip — maskPropertyStatus 경유로 분양예정만 노출
const StatusChip: React.FC<{ status?: string | null; isPreRegistration?: boolean; isReopen?: boolean; small?: boolean }> = ({
  status, isPreRegistration, isReopen, small,
}) => {
  const label = maskPropertyStatus(status, !!isPreRegistration, !!isReopen);
  if (!label) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full bg-accent-50 border border-accent-line font-bold text-accent-dark ${
        small ? 'px-2 py-0.5 text-[10.5px]' : 'px-2.5 py-1 text-[11.5px]'
      }`}
    >
      {label}
    </span>
  );
};

const ITEMS_PER_PAGE = 12;

// 주소에서 지역명 추출
const extractRegion = (address: string): string => {
  const metroMap: Record<string, string> = {
    '서울': '서울', '부산': '부산', '대구': '대구', '인천': '인천',
    '광주': '광주', '대전': '대전', '울산': '울산', '세종': '세종',
  };
  const provinceMap: Record<string, string> = {
    '경기': '경기', '강원': '강원', '충청북': '충북', '충북': '충북',
    '충청남': '충남', '충남': '충남', '전라북': '전북', '전북': '전북',
    '전라남': '전남', '전남': '전남', '경상북': '경북', '경북': '경북',
    '경상남': '경남', '경남': '경남', '제주': '제주',
  };

  for (const [key, val] of Object.entries(metroMap)) {
    if (address.includes(key)) return val;
  }
  for (const [key, val] of Object.entries(provinceMap)) {
    if (address.includes(key)) return val;
  }
  return '기타';
};

type StatusFilter = '전체' | '분양중' | '분양예정' | '입주예정';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('전체');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [showNearbyModal, setShowNearbyModal] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef<HTMLDivElement>(null);

  // 스페셜 & 일반 현장 분리
  const specialProperties = useMemo(() =>
    properties.filter(p => p.is_special).sort((a, b) => (a.special_order || 0) - (b.special_order || 0)),
    [properties]
  );

  // 상태 필터 적용
  const filteredProperties = useMemo(() => {
    let list = properties.filter(p => !p.is_special);
    if (statusFilter !== '전체') {
      if (statusFilter === '입주예정') {
        list = list.filter(p => p.status === '입주예정' || p.status === '입주완료');
      } else {
        list = list.filter(p => statusFilter === '분양중' ? isSellingNow(p as any) : p.status === statusFilter);
      }
    }
    if (regionFilter) {
      list = list.filter(p => extractRegion(p.address) === regionFilter);
    }
    return list;
  }, [properties, statusFilter, regionFilter]);

  // 분양중 현장 (수평 스크롤용)
  const activeProperties = useMemo(() =>
    properties.filter(p => !p.is_special && isSellingNow(p as any)).slice(0, 10),
    [properties]
  );

  // 분양예정 현장 (수평 스크롤용)
  const upcomingProperties = useMemo(() =>
    properties.filter(p => !p.is_special && p.status === '분양예정').slice(0, 10),
    [properties]
  );

  const displayedProperties = useMemo(() =>
    filteredProperties.slice(0, displayedCount),
    [filteredProperties, displayedCount]
  );

  const hasMore = displayedCount < filteredProperties.length;

  useEffect(() => {
    loadProperties();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProperties = async () => {
    try {
      const response = await propertyApi.getMapData();
      const propertiesList = (response as Property[]).filter(p => p.status !== '분양완료');

      // 지역 추출
      const regions = new Set<string>();
      propertiesList.forEach(p => regions.add(extractRegion(p.address)));
      const regionOrder = ['서울', '부산', '대구', '인천', '광주', '대전', '울산', '세종', '경기', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주', '기타'];
      const sortedRegions = Array.from(regions).sort((a, b) => {
        const iA = regionOrder.indexOf(a);
        const iB = regionOrder.indexOf(b);
        return (iA === -1 ? 999 : iA) - (iB === -1 ? 999 : iB);
      });

      setProperties(propertiesList);
      setAvailableRegions(sortedRegions);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load properties:', error);
      setLoading(false);
    }
  };

  // 인피니티 스크롤
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          setLoadingMore(true);
          setTimeout(() => {
            setDisplayedCount(prev => prev + ITEMS_PER_PAGE);
            setLoadingMore(false);
          }, 100);
        }
      },
      { threshold: 0.1 }
    );
    const el = loaderRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [hasMore, loadingMore]);

  // 필터 변경 시 리셋
  useEffect(() => {
    setDisplayedCount(ITEMS_PER_PAGE);
  }, [statusFilter, regionFilter]);

  // SEO
  const pageTitle = '부냥 – 아파트 분양정보, 계약혜택 안내';
  const pageDescription = '분양정보, 계약혜택, 분양가, 타입, 분양문의, 청약일정, 모델하우스 정보를 제공합니다.';
  const pageUrl = generateCanonicalUrl('/');
  const isNoindex = shouldNoindex(location.pathname + location.search);
  const itemListData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": pageTitle,
    "description": pageDescription,
    "url": pageUrl,
    "numberOfItems": filteredProperties.length,
    "itemListElement": filteredProperties.slice(0, 10).map((property, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": property.title,
      "url": getPropertyFullUrl(property, 'https://voonang.com'),
    })),
  };

  const statusFilters: StatusFilter[] = ['전체', '분양중', '분양예정', '입주예정'];

  useSeoMeta({
    title: pageTitle,
    description: pageDescription,
    robots: isNoindex ? 'noindex, follow' : 'index, follow, max-snippet:-1, max-image-preview:large',
    ogType: 'website',
    ogTitle: pageTitle,
    ogDescription: pageDescription,
    ogUrl: pageUrl,
    ogSiteName: '부냥',
    ogLocale: 'ko_KR',
    twitterCard: 'summary',
    twitterTitle: pageTitle,
    twitterDescription: pageDescription,
  });
  useHead({
    meta: [
      { name: 'keywords', content: '신규분양, 아파트분양, 오피스텔분양, 분양정보, 청약정보, 분양가, 모델하우스, 부냥' },
      { name: 'NaverBot', content: 'index, follow' },
      { name: 'Yeti', content: 'index, follow' },
    ],
    link: [{ rel: 'canonical', href: pageUrl }],
    script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(itemListData) }],
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Phase 7: lg+ 데스크탑 통일 헤더 — 다른 페이지(메인/리스트/지도/상세/프로필)와 통일 */}
      <div className="hidden lg:block bg-paper">
        <div className="mx-auto max-w-[1240px]">
          <StickyHeader
            left={
              <div className="flex items-center gap-6">
                <BrandLogo />
                <DesktopNav />
              </div>
            }
            right={<DesktopPhonePill />}
          />
        </div>
      </div>
      <h1 className="sr-only">부냥 - 분양정보 플랫폼</h1>

      {/* 스페셜 현장 수평 스크롤 */}
      {specialProperties.length > 0 && (
        <section className="bg-white pt-5 pb-3">
          <div className="flex items-center justify-between px-4 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 bg-primary-500 rounded-full" />
              <h2 className="text-lg font-bold text-gray-900">추천 분양현장</h2>
            </div>
            <span className="text-xs text-primary-500 font-semibold bg-primary-50 px-2 py-0.5 rounded-full">{specialProperties.length}개</span>
          </div>
          <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide" style={{ scrollSnapType: 'x mandatory' }}>
            {specialProperties.map(property => (
              <button
                key={property.id}
                onClick={() => navigate(getPropertyUrl(property))}
                className="flex-shrink-0 w-72 bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-200 overflow-hidden text-left border border-gray-100"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="relative h-44">
                  {(property.image_card_url || property.image_thumbnail_url || property.image_url) ? (
                    <img
                      src={property.image_card_url || property.image_thumbnail_url || property.image_url}
                      alt={property.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-300 text-sm">
                      이미지 없음
                    </div>
                  )}
                  <div className="absolute top-2.5 left-2.5">
                    <StatusChip status={property.status} isPreRegistration={(property as any).is_pre_registration === true} isReopen={(property as any).is_reopen === true} />
                  </div>
                  {property.special_marker_text && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2.5">
                      <span className="text-white text-xs font-semibold">{property.special_marker_text}</span>
                    </div>
                  )}
                </div>
                <div className="p-3.5">
                  <h3 className="text-sm font-bold text-gray-900 truncate">{property.title}</h3>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{property.address}</p>
                  <div className="mt-2">
                    <PriceTag minPrice={property.min_price} maxPrice={property.max_price} className="text-sm" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* 분양중 현장 수평 스크롤 */}
      {!loading && activeProperties.length > 0 && (
        <section className="py-4 bg-gray-50">
          <div className="flex items-center justify-between px-4 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 bg-green-500 rounded-full" />
              <h2 className="text-lg font-bold text-gray-900">분양중</h2>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                {activeProperties.length}개
              </span>
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide" style={{ scrollSnapType: 'x mandatory' }}>
            {activeProperties.map(property => (
              <button
                key={property.id}
                onClick={() => navigate(getPropertyUrl(property))}
                className="flex-shrink-0 w-56 bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-200 overflow-hidden text-left border border-gray-100"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="relative h-36">
                  {(property.image_card_url || property.image_thumbnail_url || property.image_url) ? (
                    <img src={property.image_card_url || property.image_thumbnail_url || property.image_url} alt={property.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-300 text-xs">이미지 없음</div>
                  )}
                  <div className="absolute top-2 left-2">
                    <StatusChip status={property.status} isPreRegistration={(property as any).is_pre_registration === true} isReopen={(property as any).is_reopen === true} small />
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-bold text-gray-900 truncate">{property.title}</h3>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{property.address}</p>
                  <div className="mt-1.5">
                    <PriceTag minPrice={property.min_price} maxPrice={property.max_price} className="text-xs" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* 분양예정 현장 수평 스크롤 */}
      {!loading && upcomingProperties.length > 0 && (
        <section className="py-4 bg-white">
          <div className="flex items-center justify-between px-4 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 bg-blue-500 rounded-full" />
              <h2 className="text-lg font-bold text-gray-900">분양예정</h2>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                {upcomingProperties.length}개
              </span>
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide" style={{ scrollSnapType: 'x mandatory' }}>
            {upcomingProperties.map(property => (
              <button
                key={property.id}
                onClick={() => navigate(getPropertyUrl(property))}
                className="flex-shrink-0 w-56 bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-200 overflow-hidden text-left border border-gray-100"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="relative h-36">
                  {(property.image_card_url || property.image_thumbnail_url || property.image_url) ? (
                    <img src={property.image_card_url || property.image_thumbnail_url || property.image_url} alt={property.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-300 text-xs">이미지 없음</div>
                  )}
                  <div className="absolute top-2 left-2">
                    <StatusChip status={property.status} isPreRegistration={(property as any).is_pre_registration === true} isReopen={(property as any).is_reopen === true} small />
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-bold text-gray-900 truncate">{property.title}</h3>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{property.address}</p>
                  <div className="mt-1.5">
                    <PriceTag minPrice={property.min_price} maxPrice={property.max_price} className="text-xs" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* 구분선 */}
      <div className="h-2 bg-gray-100" />

      {/* 전체 현장 목록 */}
      <section className="px-4 pt-5 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 bg-gray-900 rounded-full" />
            <h2 className="text-lg font-bold text-gray-900">전체 현장</h2>
          </div>
          <span className="text-sm text-gray-400 font-medium">{filteredProperties.length}개</span>
        </div>

        {/* 상태 필터 칩 */}
        <div className="flex gap-2 mb-3 overflow-x-auto scrollbar-hide">
          {statusFilters.map(filter => (
            <button
              key={filter}
              onClick={() => setStatusFilter(filter)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === filter
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
              }`}
            >
              {filter}
            </button>
          ))}
          <div className="w-px h-7 bg-gray-200 flex-shrink-0 self-center mx-1" />
          <button
            onClick={() => setShowNearbyModal(true)}
            className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:border-gray-400"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" />
            </svg>
            내 주변
          </button>
        </div>

        {/* 지역 필터 칩 */}
        {availableRegions.length > 0 && (
          <div className="flex gap-1.5 mb-4 overflow-x-auto scrollbar-hide pb-1">
            <button
              onClick={() => setRegionFilter('')}
              className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                !regionFilter
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              전국
            </button>
            {availableRegions.map(region => (
              <button
                key={region}
                onClick={() => setRegionFilter(regionFilter === region ? '' : region)}
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  regionFilter === region
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {region}
              </button>
            ))}
          </div>
        )}

        {/* 로딩 상태 */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse border border-gray-100">
                <div className="aspect-[4/3] bg-gray-200" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredProperties.length === 0 ? (
          <EmptyState
            title="검색 결과가 없습니다"
            description="다른 필터를 선택해보세요"
            actionLabel="필터 초기화"
            onAction={() => { setStatusFilter('전체'); setRegionFilter(''); }}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {displayedProperties.map(property => (
                <DSPropertyCard
                  key={property.id}
                  data={toHomeCardData(property)}
                  variant="compact"
                  onClick={() => navigate(getPropertyUrl(property))}
                  className="!w-auto"
                />
              ))}
            </div>

            {/* 인피니티 스크롤 로더 */}
            {hasMore && (
              <div ref={loaderRef} className="flex justify-center py-6">
                {loadingMore ? (
                  <div className="w-6 h-6 border-2 border-gray-200 border-t-primary-500 rounded-full animate-spin" />
                ) : (
                  <div className="h-1" />
                )}
              </div>
            )}
            {!hasMore && filteredProperties.length > ITEMS_PER_PAGE && (
              <p className="text-center text-sm text-gray-400 py-6 border-t border-gray-100 mt-4">
                모든 현장을 불러왔습니다
              </p>
            )}
          </>
        )}
      </section>

      <NearbyPropertiesModal
        isOpen={showNearbyModal}
        onClose={() => setShowNearbyModal(false)}
      />
    </div>
  );
};

export default HomePage;
